'use client'

import { Close } from 'components/Svgs/Close'
import classNames from 'classnames'
import { styled } from '@mui/material/styles'
import MuiAccordion from '@mui/material/Accordion'
import MuiAccordionSummary from '@mui/material/AccordionSummary'
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp'
import { useEffect, useState } from 'react'
import MuiAccordionDetails from '@mui/material/AccordionDetails'
import { Typography } from 'components/Contentful'
import SubnavDefaultSection from './SubnavDefaultSection'
import SubnavPanelSection from './SubnavPanelSection'
import { AchieveLink } from '@/components/AchieveLink'
import { useActiveNav } from '../context/ActiveNavContext'

const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(() => ({
  border: `0px`,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&::before': {
    display: 'none',
  },
}))

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
    {...props}
  />
))(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'flex-start',
  paddingLeft: 0,
  '& .MuiAccordionSummary-content': {
    margin: 0,
    flexGrow: 0,
    marginRight: theme.spacing(1),
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    marginLeft: 0,
    order: 2,
  },
  '& .MuiAccordionSummary-expandIconWrapper.Mui-collapsed': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(270deg)',
  },
}))

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '0px',
}))

const NavigationItems = ({ navigationItems, expanded, handleChange }) => {
  const { isActive, isActiveBranch } = useActiveNav()
  const componentMap = []

  for (const item of navigationItems) {
    const Component = item.variant === 'list' ? SubnavDefaultSection : SubnavPanelSection

    if (item?.children?.length > 0) {
      componentMap.push(
        <Accordion key={item.id} expanded={expanded === item.id} onChange={handleChange(item.id)}>
          <AccordionSummary aria-controls="panel1d-content" id="panel1d-header" className="px-4">
            <Typography
              content={item.label}
              variant="bodyMd"
              fontWeight="bold"
              className={classNames({ 'text-blue-900': isActiveBranch(item) })}
            />
          </AccordionSummary>
          <AccordionDetails className="p-0">
            <Component data={item} isMobile={true} />
          </AccordionDetails>
        </Accordion>
      )
    } else {
      componentMap.push(
        <Accordion key={item.id} expanded={false} onChange={handleChange(item.id)}>
          <AchieveLink
            href={item.link}
            key={item.id}
            aria-current={isActive(item) ? 'page' : undefined}
          >
            <AccordionSummary
              expandIcon={null}
              aria-controls="panel1d-content"
              id="panel1d-header"
              className="px-4"
            >
              <Typography
                content={item.label}
                variant="bodyMd"
                fontWeight="bold"
                className={classNames(isActiveBranch(item) ? 'text-blue-900' : 'text-[#1d252f]')}
              />
            </AccordionSummary>
          </AchieveLink>
        </Accordion>
      )
    }
  }

  return <div className="flex flex-col gap-2 w-full">{componentMap}</div>
}

export const Sidebar = ({ iconHeader, open, handleClose, bottomCtas, navigationItems }) => {
  const [expanded, setExpanded] = useState(null)

  const handleChange = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false)
  }

  useEffect(() => {
    if (typeof document === 'undefined') return

    if (open) {
      const previousOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = previousOverflow
      }
    }
  }, [open])

  return (
    <div
      data-testid="mega-menu-sidebar"
      className={classNames(
        'fixed z-9999 top-0 left-0 h-screen bg-white w-0 flex flex-col px-0 py-2 overflow-hidden transition-all duration-300 ease-in-out',
        { 'w-screen': open }
      )}
    >
      {open && (
        <div className="flex flex-row gap-2 justify-between items-center w-full border-0 border-b-2 border-gray-200 border-solid mt-4 pb-4 shrink-0 px-4">
          {iconHeader}
          <button onClick={handleClose} className="border-none bg-transparent">
            <Close title="Close sidebar" width={18} height={18} />
          </button>
        </div>
      )}
      {open && (
        <div className="flex flex-col gap-2 w-full flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain py-2">
          <NavigationItems
            navigationItems={navigationItems}
            expanded={expanded}
            handleChange={handleChange}
          />
        </div>
      )}
      {open && (
        <div className="flex flex-col gap-2 w-full shrink-0 pt-2 pb-4 bg-white px-4">
          {bottomCtas}
        </div>
      )}
    </div>
  )
}

export default Sidebar
